import React, { useState, useEffect } from 'react'
import { useAuthStore } from '../../../store/authStore'
import LoadingSpinner from '../../../components/common/LoadingSpinner'
import Button from '../../../components/common/Button'
import toast from 'react-hot-toast'
import {
  UsersIcon,
  DocumentDuplicateIcon,
  ChartBarIcon,
  EyeIcon,
  PencilIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
  EnvelopeIcon,
  PlusIcon
} from '@heroicons/react/24/outline'
import { db } from '../../../services/firebase'
import { collection, getDocs, updateDoc, doc, query, where, deleteDoc, setDoc, serverTimestamp, onSnapshot } from 'firebase/firestore'
import { buildPublicPath } from '../../../utils/url'

// Mock data for admin dashboard
const mockUsers = [
  {
    id: '1',
    displayName: 'Juan Pérez',
    email: 'juan@example.com',
    role: 'user',
    plan: 'pro',
    active: true,
    createdAt: '2024-01-15T10:30:00Z',
    vCardsCount: 3,
    lastLogin: '2024-01-20T15:45:00Z'
  },
  {
    id: '2',
    displayName: 'María García',
    email: 'maria@example.com',
    role: 'user',
    plan: 'free',
    active: true,
    createdAt: '2024-01-10T08:15:00Z',
    vCardsCount: 1,
    lastLogin: '2024-01-19T12:30:00Z'
  },
  {
    id: '3',
    displayName: 'Carlos Rodríguez',
    email: 'carlos@example.com',
    role: 'user',
    plan: 'premium',
    active: false,
    createdAt: '2024-01-05T14:20:00Z',
    vCardsCount: 5,
    lastLogin: '2024-01-18T09:15:00Z'
  }
]

const mockTemplates = [
  {
    id: 'template-1',
    name: 'Presentación Profesional',
    type: 'presentation',
    isActive: true,
    usageCount: 45,
    createdAt: '2024-01-01T00:00:00Z'
  },
  {
    id: 'template-2',
    name: 'Servicios Premium',
    type: 'services',
    isActive: true,
    usageCount: 32,
    createdAt: '2024-01-01T00:00:00Z'
  },
  {
    id: 'template-3',
    name: 'Landing Personal',
    type: 'landing',
    isActive: true,
    usageCount: 28,
    createdAt: '2024-01-01T00:00:00Z'
  }
]

function AdminDashboard() {
  const { user } = useAuthStore()
  const [loading, setLoading] = useState(true)
  const [users, setUsers] = useState([])
  const [templates, setTemplates] = useState(mockTemplates)
  const [selectedTab, setSelectedTab] = useState('users')
  const [contacts, setContacts] = useState([])
  const [vCards, setVCards] = useState([])
  const [usersById, setUsersById] = useState({})
  const [plans, setPlans] = useState([])
  const [showNewPlan, setShowNewPlan] = useState(false)
  const [newPlan, setNewPlan] = useState({ name: '', cards: 1, price: 0, discountPrice: '', active: true })
  const defaultServiceFeatures = [
    'Dkard Personalizada',
    'Integraciones y QR',
    'Catálogo y Servicios',
    'Analítica básica'
  ]

  useEffect(() => {
    let unsubUsers = null
    const init = async () => {
      try {
        unsubUsers = onSnapshot(collection(db, 'users'), async (snap) => {
          const data = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        const counts = await Promise.all(data.map(async (u) => {
          const q = query(collection(db, 'vcards'), where('userId', '==', u.id))
          const vs = await getDocs(q)
          return { uid: u.id, count: vs.size }
        }))
        const enriched = data.map(u => {
          const found = counts.find(c => c.uid === u.id)
          const computed = found ? found.count : 0
          const override = (typeof u.vCardsCountOverride === 'number') ? u.vCardsCountOverride : null
          return { ...u, vCardsCount: (override ?? computed) }
        })
        setUsers(enriched)
        setUsersById(enriched.reduce((acc, u) => { acc[u.id] = u; return acc }, {}))
        setLoading(false)
      })

        const vSnap = await getDocs(collection(db, 'vcards'))
        const vData = vSnap.docs.map(d => ({ id: d.id, ...d.data() }))
        setVCards(vData)

        const cSnap = await getDocs(collection(db, 'contacts'))
        const cData = cSnap.docs.map(d => ({ id: d.id, ...d.data() }))
        setContacts(cData)

        const pSnap = await getDocs(collection(db, 'plans'))
        const pData = pSnap.docs.map(d => ({ id: d.id, ...d.data() }))
        setPlans(pData)
      } catch (e) {
        toast.error(e?.code === 'permission-denied' ? 'Permiso denegado al cargar datos (revisa reglas Firestore)' : 'Error al cargar datos')
        setLoading(false)
      }
    }
    init()
    return () => { if (unsubUsers) unsubUsers() }
  }, [])

  const handleUserAction = async (userId, action) => {
    try {
      const current = users.find(u => u.id === userId)
      if (!current) {
        toast.error('Usuario no encontrado')
        return
      }
      const base = {
        displayName: current.displayName || current.email,
        email: current.email,
        role: current.role || 'user',
        plan: current.plan || 'free',
        active: current.active === true
      }
      if (action === 'toggleActive') {
        const nextActive = !current.active
        const patch = { ...base, active: nextActive, activatedAt: nextActive ? serverTimestamp() : null }
        await setDoc(doc(db, 'users', userId), patch, { merge: true })
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, active: nextActive } : u))
      } else if (action === 'makeAdmin') {
        const patch = { ...base, role: 'admin' }
        await setDoc(doc(db, 'users', userId), patch, { merge: true })
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: 'admin' } : u))
      } else if (action === 'removeAdmin') {
        const patch = { ...base, role: 'user' }
        await setDoc(doc(db, 'users', userId), patch, { merge: true })
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: 'user' } : u))
      } else if (action === 'upgradePlan') {
        const nextPlan = current.plan === 'free' ? 'pro' : current.plan === 'pro' ? 'premium' : 'premium'
        const patch = { ...base, plan: nextPlan }
        await setDoc(doc(db, 'users', userId), patch, { merge: true })
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, plan: nextPlan } : u))
      }
      toast.success('Usuario actualizado exitosamente')
    } catch (error) {
      toast.error(error?.code === 'permission-denied' ? 'Permiso denegado (reglas Firestore bloquean esta acción)' : 'Error al actualizar usuario')
    }
  }

  const handleTemplateAction = async (templateId, action) => {
    try {
      const updatedTemplates = templates.map(template => {
        if (template.id === templateId) {
          switch (action) {
            case 'toggleActive':
              return { ...template, isActive: !template.isActive }
            default:
              return template
          }
        }
        return template
      })
      setTemplates(updatedTemplates)
      toast.success('Plantilla actualizada exitosamente')
    } catch (error) {
      toast.error('Error al actualizar plantilla')
    }
  }

  const handleVCardAction = async (vCardId, action) => {
    try {
      if (action === 'togglePublic') {
        const current = vCards.find(v => v.id === vCardId)
        await updateDoc(doc(db, 'vcards', vCardId), { public: !current.public })
        setVCards(prev => prev.map(v => v.id === vCardId ? { ...v, public: !current.public } : v))
      } else if (action === 'toggleActive') {
        const current = vCards.find(v => v.id === vCardId)
        const isActive = current.active !== false
        await updateDoc(doc(db, 'vcards', vCardId), { active: !isActive })
        setVCards(prev => prev.map(v => v.id === vCardId ? { ...v, active: !isActive } : v))
      } else if (action === 'delete') {
        await deleteDoc(doc(db, 'vcards', vCardId))
        setVCards(prev => prev.filter(v => v.id !== vCardId))
      }
      toast.success('Dkard actualizada exitosamente')
    } catch (error) {
      toast.error('Error al actualizar Dkard')
    }
  }

  const formatDate = (ts) => {
    try {
      const d = ts?.toDate ? ts.toDate() : (ts ? new Date(ts) : null)
      return d && !isNaN(d.getTime()) ? d.toLocaleDateString() : ''
    } catch { return '' }
  }

  const getInitial = (u) => {
    if (!u) return '?'
    const base = (u.displayName || u.email || '?')
    return String(base).charAt(0).toUpperCase()
  }

  const getStats = () => {
    return {
      totalUsers: users.filter(u => u && u.id && u.email).length,
      activeUsers: users.filter(u => u && u.active).length,
      totalVCards: users.reduce((sum, user) => sum + (user.vCardsCount || 0), 0),
      totalTemplates: templates.length,
      activeTemplates: templates.filter(t => t.isActive).length
    }
  }

  const stats = getStats()

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Panel de Administración</h1>
        <p className="text-gray-600">
          Gestión completa de usuarios, plantillas y métricas del sistema
        </p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <UsersIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Usuarios</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalUsers}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <UsersIcon className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Usuarios Activos</p>
              <p className="text-2xl font-bold text-gray-900">{stats.activeUsers}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-lg">
              <DocumentDuplicateIcon className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Dkards</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalVCards}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center">
            <div className="p-3 bg-orange-100 rounded-lg">
              <ChartBarIcon className="w-6 h-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Plantillas Activas</p>
              <p className="text-2xl font-bold text-gray-900">{stats.activeTemplates}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="mb-6">
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
            {[
              { id: 'overview', name: 'Resumen', icon: ChartBarIcon },
              { id: 'users', name: 'Usuarios', icon: UsersIcon },
              { id: 'vcards', name: 'Dkards', icon: DocumentDuplicateIcon },
              { id: 'plans', name: 'Planes', icon: DocumentDuplicateIcon },
              { id: 'contacts', name: 'Contactos', icon: EnvelopeIcon },
              { id: 'templates', name: 'Plantillas', icon: DocumentDuplicateIcon }
            ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setSelectedTab(tab.id)}
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                selectedTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-5 h-5 mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>
      </div>

      {/* Content based on selected tab */}
      {selectedTab === 'overview' && (
        <div className="space-y-8">
          {/* Recent Activity */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Actividad Reciente</h2>
            <div className="space-y-4">
              <div className="flex items-center p-4 bg-gray-50 rounded-lg">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">Nuevo usuario registrado</p>
                  <p className="text-xs text-gray-600">Juan Pérez se registró hace 2 horas</p>
                </div>
                <span className="text-xs text-gray-500">Hace 2h</span>
              </div>
              
              <div className="flex items-center p-4 bg-gray-50 rounded-lg">
                <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">Dkard creada</p>
                  <p className="text-xs text-gray-600">María García creó una nueva Dkard</p>
                </div>
                <span className="text-xs text-gray-500">Hace 4h</span>
              </div>
              
              <div className="flex items-center p-4 bg-gray-50 rounded-lg">
                <div className="w-2 h-2 bg-purple-500 rounded-full mr-3"></div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">Plantilla actualizada</p>
                  <p className="text-xs text-gray-600">Se actualizó la plantilla Presentación Profesional</p>
                </div>
                <span className="text-xs text-gray-500">Hace 6h</span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Acciones Rápidas</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors">
                <UsersIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-900">Crear Usuario</p>
                <p className="text-xs text-gray-600">Agregar nuevo administrador</p>
              </button>
              
              <button className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors">
                <DocumentDuplicateIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-900">Nueva Plantilla</p>
                <p className="text-xs text-gray-600">Diseñar nueva plantilla</p>
              </button>
              
              <button className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors">
                <ChartBarIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-900">Ver Reportes</p>
                <p className="text-xs text-gray-600">Análisis detallado</p>
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedTab === 'users' && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Gestión de Usuarios</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Usuario
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Plan
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dkards</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Máx. Dkards</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Último Acceso
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.filter(user => user && user.id && user.email).map((user) => (
                  <tr key={user.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-medium">
                          {getInitial(user)}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{user.displayName || user.email}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        user.plan === 'free' ? 'bg-gray-100 text-gray-800' :
                        user.plan === 'pro' ? 'bg-blue-100 text-blue-800' :
                        'bg-purple-100 text-purple-800'
                      }`}>
                        {user.plan.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.vCardsCount}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <input
                        type="number"
                        min="1"
                        value={typeof user.maxCards === 'number' ? user.maxCards : 1}
                        className="w-20 border rounded px-2 py-1"
                        onChange={(e) => {
                          const val = Math.max(1, parseInt(e.target.value || '1', 10))
                          setUsers(prev => prev.map(u => u.id === user.id ? { ...u, maxCards: val } : u))
                        }}
                        onBlur={async (e) => {
                          const val = Math.max(1, parseInt(e.target.value || '1', 10))
                          try {
                            await updateDoc(doc(db, 'users', user.id), { maxCards: val })
                            toast.success('Máximo actualizado')
                          } catch (error) {
                            console.error('Error updating maxCards:', error)
                            toast.error('Error al actualizar máximo: ' + (error?.code || 'Sin permisos'))
                          }
                        }}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                        user.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {user.active ? (
                          <ShieldCheckIcon className="w-4 h-4 mr-1" />
                        ) : (
                          <ShieldExclamationIcon className="w-4 h-4 mr-1" />
                        )}
                        {user.active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(user.lastLogin) || formatDate(user.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleUserAction(user.id, 'toggleActive')}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          {user.active ? 'Desactivar' : 'Activar'}
                        </button>
                        <button
                          onClick={() => handleUserAction(user.id, 'upgradePlan')}
                          className="text-green-600 hover:text-green-900"
                        >
                          Plan
                        </button>
                        <button
                          onClick={() => handleUserAction(user.id, user.role === 'admin' ? 'removeAdmin' : 'makeAdmin')}
                          className="text-purple-600 hover:text-purple-900"
                        >
                          {user.role === 'admin' ? 'Quitar Admin' : 'Hacer Admin'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedTab === 'templates' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Gestión de Plantillas</h2>
              <Button variant="primary">
                <PlusIcon className="w-5 h-5 mr-2" />
                Nueva Plantilla
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {templates.map((template) => (
                <div key={template.id} className="border rounded-lg p-4">
                  <div className="aspect-w-4 aspect-h-3 bg-gray-100 rounded-lg mb-4 flex items-center justify-center">
                    <DocumentDuplicateIcon className="w-12 h-12 text-gray-400" />
                  </div>
                  
                  <h3 className="font-semibold text-gray-900 mb-2">{template.name}</h3>
                  <p className="text-sm text-gray-600 mb-4">Tipo: {template.type}</p>
                  
                  <div className="flex justify-between items-center mb-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      template.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {template.isActive ? 'Activa' : 'Inactiva'}
                    </span>
                    <span className="text-sm text-gray-500">
                      {template.usageCount} usos
                    </span>
                  </div>
                  
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleTemplateAction(template.id, 'toggleActive')}
                      className="flex-1 py-2 px-3 text-sm border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                    >
                      {template.isActive ? 'Desactivar' : 'Activar'}
                    </button>
                    <button className="flex-1 py-2 px-3 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors">
                      Editar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {selectedTab === 'contacts' && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Mensajes de Contacto</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Asunto</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mensaje</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {contacts.map((c) => (
                  <tr key={c.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{c.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{c.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{c.subject}</td>
                    <td className="px-6 py-4 whitespace-pre-wrap text-sm text-gray-700">{c.message}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${c.status === 'new' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>{c.status || 'new'}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button onClick={async () => { await updateDoc(doc(db, 'contacts', c.id), { status: 'done' }); setContacts(prev => prev.map(x => x.id === c.id ? { ...x, status: 'done' } : x)) }} className="text-green-600 hover:text-green-900">Marcar como hecho</button>
                        <button onClick={async () => { await deleteDoc(doc(db, 'contacts', c.id)); setContacts(prev => prev.filter(x => x.id !== c.id)) }} className="text-red-600 hover:text-red-900">Eliminar</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedTab === 'plans' && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Planes</h2>
            <div className="flex items-center gap-3">
              <button
              onClick={async () => {
                try {
                  const defaults = [
                    { id: 'plan-personal', name: 'Personal', cards: 1, price: 250, discountPrice: null, features: ['Dkard básica','QR personalizado','Compartir por WhatsApp'], active: true },
                    { id: 'plan-servicios', name: 'Servicios', cards: 3, price: 350, discountPrice: null, features: ['Servicios','Testimonios','FAQ'], active: true },
                    { id: 'plan-catalogo', name: 'Catálogo', cards: 5, price: 450, discountPrice: null, features: ['Grid de productos','Carrito ligero'], active: true },
                  ]
                  for (const p of defaults) {
                    await setDoc(doc(db, 'plans', p.id), p, { merge: true })
                  }
                  const pSnap = await getDocs(collection(db, 'plans'))
                  const pData = pSnap.docs.map(d => ({ id: d.id, ...d.data() }))
                  setPlans(pData)
                  toast.success('Planes iniciales guardados')
                } catch (e) {
                  if (e?.code === 'permission-denied') toast.error('No tienes permisos (rol admin requerido)')
                  else toast.error('Error al guardar planes')
                }
              }}
              className="btn-secondary"
            >Seed 3 planes</button>
              <button
                onClick={() => setShowNewPlan(v => !v)}
                className="btn-primary"
              >Nuevo Plan</button>
            </div>
          </div>
          {showNewPlan && (
            <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Nombre</label>
                  <input
                    type="text"
                    value={newPlan.name}
                    onChange={e => setNewPlan(p => ({ ...p, name: e.target.value }))}
                    className="w-full border rounded px-2 py-1"
                    placeholder="Nuevo plan"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Máx. Dkards</label>
                  <input
                    type="number"
                    min="1"
                    value={newPlan.cards}
                    onChange={e => setNewPlan(p => ({ ...p, cards: Math.max(1, parseInt(e.target.value || '1', 10)) }))}
                    className="w-full border rounded px-2 py-1"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Precio (Bs.)</label>
                  <input
                    type="number"
                    min="0"
                    value={newPlan.price}
                    onChange={e => setNewPlan(p => ({ ...p, price: Math.max(0, parseInt(e.target.value || '0', 10)) }))}
                    className="w-full border rounded px-2 py-1"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Descuento (Bs.)</label>
                  <input
                    type="number"
                    min="0"
                    value={newPlan.discountPrice === '' ? '' : newPlan.discountPrice}
                    onChange={e => {
                      const v = e.target.value
                      setNewPlan(p => ({ ...p, discountPrice: v === '' ? '' : Math.max(0, parseInt(v, 10)) }))
                    }}
                    className="w-full border rounded px-2 py-1"
                    placeholder="—"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={async () => {
                      try {
                        const baseId = (newPlan.name || 'plan-nuevo')
                          .toLowerCase()
                          .replace(/\s+/g, '-')
                          .replace(/[^a-z0-9\-]/g, '') || 'plan-nuevo'
                        const id = plans.some(p => p.id === baseId) ? `${baseId}-${Date.now()}` : baseId
                        const payload = {
                          id,
                          name: newPlan.name || 'Nuevo',
                          cards: newPlan.cards || 1,
                          price: newPlan.price || 0,
                          discountPrice: newPlan.discountPrice === '' ? null : (newPlan.discountPrice || null),
                          active: true,
                          features: defaultServiceFeatures
                        }
                    await setDoc(doc(db, 'plans', id), payload, { merge: true })
                    setPlans(prev => [...prev, payload])
                    setShowNewPlan(false)
                    setNewPlan({ name: '', cards: 1, price: 0, discountPrice: '', active: true })
                    toast.success('Plan creado')
                      } catch (e) {
                        if (e?.code === 'permission-denied') toast.error('No tienes permisos (rol admin requerido)')
                        else toast.error('No se pudo crear el plan')
                      }
                    }}
                    className="btn-primary"
                  >Crear</button>
                  <button onClick={() => setShowNewPlan(false)} className="btn-secondary">Cancelar</button>
                </div>
              </div>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Máx. Dkards</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Precio (Bs.)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descuento (Bs.)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Opciones</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Activo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {plans.map((p) => (
                  <tr key={p.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{p.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <input type="number" min="1" value={typeof p.cards === 'number' ? p.cards : 1} className="w-20 border rounded px-2 py-1"
                        onChange={(e) => {
                          const val = Math.max(1, parseInt(e.target.value || '1', 10))
                          setPlans(prev => prev.map(x => x.id === p.id ? { ...x, cards: val } : x))
                        }}
                        onBlur={async (e) => {
                          const val = Math.max(1, parseInt(e.target.value || '1', 10))
                          try {
                            await updateDoc(doc(db, 'plans', p.id), { cards: val })
                            toast.success('Máximo actualizado')
                          } catch { toast.error('Sin permisos para editar') }
                        }} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <input type="number" min="0" value={typeof p.price === 'number' ? p.price : 0} className="w-24 border rounded px-2 py-1"
                        onChange={(e) => {
                          const val = Math.max(0, parseInt(e.target.value || '0', 10))
                          setPlans(prev => prev.map(x => x.id === p.id ? { ...x, price: val } : x))
                        }}
                        onBlur={async (e) => {
                          const val = Math.max(0, parseInt(e.target.value || '0', 10))
                          try {
                            await updateDoc(doc(db, 'plans', p.id), { price: val })
                            toast.success('Precio actualizado')
                          } catch { toast.error('Sin permisos para editar') }
                        }} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <input type="number" min="0" value={p.discountPrice ?? ''} placeholder="—" className="w-24 border rounded px-2 py-1"
                        onChange={(e) => {
                          const v = e.target.value
                          const val = v === '' ? null : Math.max(0, parseInt(v, 10))
                          setPlans(prev => prev.map(x => x.id === p.id ? { ...x, discountPrice: val } : x))
                        }}
                        onBlur={async (e) => {
                          const v = e.target.value
                          const val = v === '' ? null : Math.max(0, parseInt(v, 10))
                          try {
                            await updateDoc(doc(db, 'plans', p.id), { discountPrice: val })
                            toast.success('Descuento actualizado')
                          } catch { toast.error('Sin permisos para editar') }
                        }} />
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <textarea
                        className="w-56 border rounded px-2 py-1 h-20"
                        value={(p.features || []).join('\n')}
                        onChange={(e) => {
                          const lines = e.target.value.split(/\n+/).map(s => s.trim()).filter(Boolean)
                          setPlans(prev => prev.map(x => x.id === p.id ? { ...x, features: lines } : x))
                        }}
                        onBlur={async (e) => {
                          const lines = e.target.value.split(/\n+/).map(s => s.trim()).filter(Boolean)
                          try {
                            await updateDoc(doc(db, 'plans', p.id), { features: lines })
                            toast.success('Opciones actualizadas')
                          } catch { toast.error('Sin permisos para editar') }
                        }}
                        placeholder={"Una por línea\nUsa **negrita** o *cursiva*"}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <button onClick={async () => { try { await updateDoc(doc(db, 'plans', p.id), { active: !(p.active !== false) }); setPlans(prev => prev.map(x => x.id === p.id ? { ...x, active: !(p.active !== false) } : x)); toast.success('Estado actualizado') } catch { toast.error('Sin permisos para editar') } }} className="text-blue-600 hover:text-blue-900">
                         {(p.active !== false) ? 'Desactivar' : 'Activar'}
                       </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <button
                        onClick={async () => {
                          try {
                            await deleteDoc(doc(db, 'plans', p.id))
                            setPlans(prev => prev.filter(x => x.id !== p.id))
                            toast.success('Plan eliminado')
                          } catch { toast.error('Sin permisos para eliminar') }
                        }}
                        className="text-red-600 hover:text-red-900"
                      >Eliminar</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedTab === 'vcards' && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Gestión de Dkards</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuario</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Slug</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Activa</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Creada</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {vCards.map((v) => (
                  <tr key={v.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{v.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{usersById[v.userId]?.email || usersById[v.userId]?.displayName || v.userId}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{v.slug}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${v.public ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {v.public ? 'Pública' : 'Privada'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${(v.active !== false) ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {(v.active !== false) ? 'Activa' : 'Deshabilitada'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(v.createdAt)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <a href={`${(typeof window !== 'undefined' && window.location ? window.location.origin : '')}${buildPublicPath(v.slug)}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-900">Ver</a>
                        <button onClick={() => handleVCardAction(v.id, 'togglePublic')} className="text-green-600 hover:text-green-900">{v.public ? 'Hacer privada' : 'Hacer pública'}</button>
                        <button onClick={() => handleVCardAction(v.id, 'toggleActive')} className="text-purple-600 hover:text-purple-900">{(v.active !== false) ? 'Deshabilitar' : 'Habilitar'}</button>
                        <button onClick={() => handleVCardAction(v.id, 'delete')} className="text-red-600 hover:text-red-900">Eliminar</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminDashboard
